import {
  type NavigationGuardNext,
  type RouteLocationNormalized,
  createRouter,
  createWebHistory,
} from 'vue-router'
import DashboardView from '@/views/DashboardView.vue'
import { useFirebaseStore } from '@/stores/firebase'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      // Redirect root to dashboard
      path: '/',
      redirect: '/login',
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/SignIn.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/dashboard',
      name: 'home',
      component: DashboardView,
      meta: { requiresAuth: true },
      children: [
        {
          // This is how to show a default child component when the parent is loaded
          path: '',
          name: 'dashboard-home',
          component: () => import('@/components/dashboard/DashboardHome.vue'),
        },
        {
          path: 'insights',
          name: 'dashboard-insights',
          component: () => import('@/components/dashboard/InsightsTab.vue'),
        },
        {
          path: 'flights',
          name: 'dashboard-flights',
          component: () => import('@/components/dashboard/FlightsTab.vue'),
        },
        {
          path: 'invoices',
          name: 'dashboard-invoices',
          component: () => import('@/components/dashboard/InvoicesTab.vue'),
        },
        {
          path: 'travelers',
          name: 'dashboard-travelers',
          component: () => import('@/components/dashboard/TravelersTab.vue'),
        },
        {
          path: 'help',
          name: 'dashboard-help',
          component: () => import('@/components/dashboard/HelpTab.vue'),
        },
      ],
    },
    {
      path: '/:pathMatch(.*)*', // Catch all 404
      name: 'not-found',
      component: () => import('@/views/NotFound.vue'),
    },
  ],
})
export async function authGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
) {
  const firebaseStore = useFirebaseStore()

  // Make sure auth is initialized before proceeding
  if (!firebaseStore.authInitialized) {
    // This should rarely happen as main.ts waits for initialization,
    // but it's a good safety check
    console.warn('Auth not yet initialized during navigation')
    return next(false)
  }

  const isAuthenticated = firebaseStore.isAuthenticated
  const hasAuthError = firebaseStore.hasAuthError

  // Default to requiring auth unless explicitly set to false
  const requiresAuth = to.meta.requiresAuth !== false

  // Handle auth errors (e.g., token validation failures)
  if (hasAuthError && to.name !== 'login') {
    console.warn('Auth error detected, redirecting to login')
    return next({
      name: 'login',
      query: {
        redirect: to.fullPath,
        authError: 'true',
      },
    })
  }

  // Handle authentication redirects
  if (requiresAuth && !isAuthenticated) {
    console.log('Auth required but user not authenticated, redirecting to login')
    return next({
      name: 'login',
      query: { redirect: to.fullPath },
    })
  }

  // Prevent authenticated users from accessing login page
  if (isAuthenticated && to.name === 'login') {
    console.log('User already authenticated, redirecting to dashboard')
    return next({ name: 'dashboard-home' })
  }

  // Check company ID if route requires it
  if (requiresAuth && isAuthenticated && to.meta.requiresCompany !== false) {
    const hasCompanyId = !!firebaseStore.currentCompanyId

    if (!hasCompanyId) {
      console.warn('Company ID required but not available')
      // Handle missing company ID - could redirect to a specific error page
      // or a company selection page if the user belongs to multiple companies
      return next({ name: 'company-error' })
    }
  }

  // Allow the navigation to proceed
  return next()
}

router.beforeEach(authGuard)
export default router
